<template>
    <div class="main">
        <Header ref="header" />
        <router-view />
        <alert-dialog ref="alertDialog" />
        <connection-overlay ref="refConnectionOverlay"/>
    </div>
</template>

<style scoped lang="scss">
    .main {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";
import { DeviceStatus, SignalR } from "./support/SignalR";
import Header from "@/components/Header.vue";
import AlertDialog, { initializeAlertDialog, isAlertDialogOpen, showAlertDialog } from "@/shared/components/common/AlertDialog.vue";
import { EMPTY, Observable } from "rxjs";
import { AUGGIEAVAILABLE } from "./main";
import Home from "./views/Home.vue";
import { CalibrationSessionDto } from "@/shared/models/CalibrationSessionDto";
import CameraViewerConfiguration from "@/shared/models/CameraViewerConfiguration";
import { Global } from "./support/GlobalData";
import { DataRequest } from "@/shared/support/Data";
import Utility from "@/shared/support/Utility";
import PageBase from "./shared/components/common/PageBase";
import { killHelpModal } from "./views/HelpModal.vue";
import { UpdateResultDto } from "./shared/models/UpdateResultDto";
import { CalibrationEventSupport } from "./support/CalibrationEvent";
import Toast, { FeedbackFormat, ToastFeedback } from "@/shared/support/Toast";
import { CalibrationEvent } from "./shared/enums/CalibrationEvent";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import { CPUDEGRADEDMESSAGE, isCpuDegraded, isCpuHealthy } from "@/shared/support/CpuInfo";
import ConnectionOverlay from "./components/ConnectionOverlay.vue";
import OverlaySuppression from "@/shared/support/OverlaySuppression";

// Static accessors for MainApp, MainHeader, MainHome
const AppStatic = {
    get MainApp(): any {
        return Global.MainApp;
    },
    get MainHeader(): InstanceType<typeof Header> {
        return Global.MainHeader;
    },
    get MainHome(): InstanceType<typeof Home> {
        return Global.MainHome;
    }
};

export default defineComponent({
    name: "App",
    components: {
        Header,
        AlertDialog,
        ConnectionOverlay
    },
    subscriptions(): any {
        if (!AUGGIEAVAILABLE) return []; // debugging stand-alone only
        return {
            auggieNetworkConnected$: Auggie.Network.Connected
        };
    },
    data() {
        return {
            less20WasShown: false,
            less10WasShown: false,
            batteryToast: null as any,
            batteryInterval: 0,
            batteryLastLevel: 0,
            beepInterval: 0,
            temperatureInterval: 0,
            cpuInterval: 0,
            cpuAlertArmed: true,
            verificationInterval: 0,
            restarting: false,
            cellularSignalInterval: 0,
            cellularInfoInterval: 0,
            auggieNetworkConnected$: EMPTY as Observable<boolean>,
            // AIR low battery auto-shutdown
            shutdownCountdown: 0,
            shutdownInterval: 0,
            shutdownToast: null as any,
            shutdownCancelled: false
        };
    },
    computed: {
        networkConnected(): boolean {
            if (!AUGGIEAVAILABLE) return true; // debugging stand-alone only
            if (this.auggieNetworkConnected$ === null) return false;
            return this.auggieNetworkConnected$ as unknown as boolean;
        }
    },
    created(): void {
        Global.MainApp = this;
        Utility.setDefaultTimeZone(import.meta.env.VITE_TimeZone);
        Utility.setDefaultDateFormat(import.meta.env.VITE_DateFormat);
    },
    mounted(): void {
        initializeAlertDialog((this.$refs as any).alertDialog);

        if (AUGGIEAVAILABLE) {
            Global.DeviceSerialNumber = Auggie.Device.SerialNumber();
            Global.Environment = Auggie.System.GetEnvironment();
        } else {
            Global.DeviceSerialNumber = "EDkWCuGSYz7W9KgpABTz"; // default test device
            Global.Environment = "DEV";
        }

        // Shut down TeamViewer
        if (AUGGIEAVAILABLE && Global.IsVersionAtLeast("2022.286.2")) {
            Auggie.TeamViewer.Off();
        }

        // Init signalr
        SignalR.start(Global.GetSignalRFunctionUrl());
        SignalR.onSignalRReconnecting = this.signalRReconnecting;
        SignalR.onSignalRReconnected = this.signalRReconnected;
        SignalR.onSignalRDisconnected = this.signalRDisconnected;

        DataRequest.getWebAPI = (): string => {
            const domain = Global.GetWebAPIUrl();
            if (!domain) throw new Error("Global.GetWebAPIUrl() didn't return a URL");
            return domain;
        };
        DataRequest.setRequestHeaders = (request: XMLHttpRequest, useAuthentication: boolean): void => {
            request.setRequestHeader("Device-Api-Version", "v1.0");
            request.setRequestHeader(import.meta.env.VITE_DEVICE_SERIAL_NUMBER_HEADER!, Global.DeviceSerialNumber);
            request.setRequestHeader("Authorization", `Bearer ${DataRequest.getToken!()}`);
        };
        DataRequest.getToken = (): string => {
            let token: string;
            if (AUGGIEAVAILABLE) {
                token = Auggie.System.GetToken();
            } else {
                token = import.meta.env.VITE_WEBAPITOKEN || "no token";
            }
            return token;
        };
        DataRequest.haveToken = (): boolean => {
            return true;
        };
        DataRequest.getMaxRetries = (): number => {
            return 10; // 10 retries
        };
        DataRequest.getDetailLog = (): boolean => {
            return Global.DetailLog;
        };

        PageBase.NewPageShownCallback = (): void => {
            killHelpModal();
        };

        this.setBatteryInfo();
        this.setTemperatureInfo();
        this.setCpuInfo();
        this.setCellularPolls();
        this.setSessionVerification();

        SignalR.onRestartCalibrationFallback = (): void => {
            if (Global.CalibrationSession != null)
                this.$router.push("/Calibration/CameraReady");
        };
        SignalR.onBatteryLowAcknowledged20 = (): void => {
            this.dismissBattery20ToastCompanion();
        };
        SignalR.onBatteryLowAcknowledged10 = (): void => {
            this.dismissBattery10ToastCompanion();
        };

        OverlaySuppression.onChange = this.setCameraWindowSuppressed;
    },
    unmounted(): void {
        this.clearBeeping();
        this.clearShutdownCountdown();
    },
    methods: {
        // Session Management

        setSession(session: CalibrationSessionDto): void {
            Global.CalibrationSession = session;
        },
        setCameraConfig(cameraConfig: CameraViewerConfiguration): void {
            Global.CameraViewerConfiguration = cameraConfig;
            // save the original windshield if this is the first time
            if (Global.OriginalWindshieldAngle == null)
                Global.OriginalWindshieldAngle = cameraConfig.CarWindshieldAngle;
        },
        clearSession(): void {
            if (AUGGIEAVAILABLE) {
                Auggie.Target.ClearTarget();
                if (Global.IsVersionAtLeast("0.9.15"))
                    Auggie.Lights.SetIntensity(0);
                else
                    Auggie.Camera.LightOff();
                Auggie.Camera.TerminateRender();
            } else {
                // nothing to do
            }
            Global.clear();
        },
        haveSession(): boolean {
            return !!Global.CalibrationSession && Global.CalibrationSession.SessionStartDt != null;
        },
        setDemoSession(): void {
            Global.CalibrationSession = this.getFakeDemoSession();
            Global.CalibrationSession.SessionStartDt = null;// not a real session yet
        },
        getFakeDemoSession(): CalibrationSessionDto {
            return {
                CalibrationSessionGuid: "000000000-0000-0000-0000-000000000000",
                SessionStartDt: new Date().toUTCString(),
                CreatedDt: new Date().toUTCString(),
                WheelArchHeightEnabledInd: true,
            } as CalibrationSessionDto;
        },

        setBatteryInfo(): void {
            if (!this.batteryInterval) {
                const interval = Number(import.meta.env.VITE_BATTERYINFOINTERVAL!);
                if (!interval) return;
                this.getBatteryInfo();// first time retrieval
                if (!Global.BatteryInfo) return; // no battery info available
                if (Global.DetailLog) console.log(`setBatteryInfo: Battery info retrieval every ${interval} seconds`);
                this.batteryInterval = setInterval((): void => {
                    this.getBatteryInfo();
                }, interval * 1000) as unknown as number;
            }
        },
        clearBatteryInfo(): void {
            if (this.batteryInterval) {
                clearInterval(this.batteryInterval);
                this.batteryInterval = 0;
            }
        },
        getBatteryInfo(): void {
            // retrieve battery info
            if (!AUGGIEAVAILABLE)
                return; // no battery information available
            if (!Global.IsVersionAtLeast(Global.BatteryPackageVersion))
                return; // old package, don't retrieve battery info
            if (Global.DetailLog) console.log("getBatteryInfo: retrieving battery information");
            const infoString = Auggie.Device.BatteryInfo();
            if (infoString?.length > 0) {
                try {
                    const info = JSON.parse(infoString);
                    Global.BatteryInfo = info;
                } catch (error) {
                    Global.BatteryInfo = null;
                }
            }
            if (Global.DetailLog) console.log(`getBatteryInfo: battery information ${infoString}`);
            if (AppStatic.MainHeader) AppStatic.MainHeader.update();

            CalibrationEventSupport.sendBatteryInformation();

            if (Global.BatteryInfo) {
                if (!Global.BatteryInfo.ExternalPowerInd) {
                    if (Global.BatteryInfo.PercentageFull >= 0 && Global.BatteryInfo.PercentageFull <= 10) {
                        if (!this.less10WasShown || (this.batteryToast && this.batteryLastLevel !== Global.BatteryInfo.PercentageFull)) {
                            this.less10WasShown = this.less20WasShown = true;
                            if (Global.BatteryInfo.PercentageFull <= 5)
                                this.startBeeping10Seconds();
                            else
                                this.startBeeping20Seconds();
                            this.showBatteryToast();
                        }
                        // AIR auto-shutdown at 5% battery
                        if (Global.IsAir() && Global.BatteryInfo.PercentageFull <= 5) {
                            if (!this.shutdownInterval && !this.shutdownCancelled) {
                                this.startShutdownCountdown();
                            }
                        }
                    } else if (Global.BatteryInfo.PercentageFull > 10 && Global.BatteryInfo.PercentageFull < 20) {
                        if (!this.less20WasShown || (this.batteryToast && this.batteryLastLevel !== Global.BatteryInfo.PercentageFull)) {
                            this.less20WasShown = true;
                            this.showBatteryToast();
                        }
                    } else {
                        this.clearBeeping();
                        this.clearBatteryToast();
                        this.less10WasShown = this.less20WasShown = false;
                        // Reset shutdown cancelled flag when battery goes above 5%
                        if (Global.IsAir() && Global.BatteryInfo.PercentageFull > 5) {
                            this.shutdownCancelled = false;
                        }
                    }
                } else {
                    this.clearBeeping();
                    this.clearBatteryToast();
                    this.less10WasShown = this.less20WasShown = false;
                    // Cancel shutdown if external power connected
                    if (Global.IsAir()) {
                        this.clearShutdownCountdown();
                        this.shutdownCancelled = false;
                    }
                }
            }
        },
        startBeeping20Seconds(): void {
            if (!Global.BatteryInfo) return;
            if (this.beepInterval) return;
            this.playBeep(); // play initial beep
            this.beepInterval = setInterval((): void => {
                if (Global.BatteryInfo!.PercentageFull <= 5) {
                    this.clearBeeping();
                    this.startBeeping10Seconds();
                } else {
                    this.playBeep();
                }
            }, 20* 1000) as unknown as number;
        },
        startBeeping10Seconds(): void {
            if (!Global.BatteryInfo) return;
            if (this.beepInterval) return;
            this.playBeep(); // play initial beep
            this.beepInterval = setInterval((): void => {
                this.playBeep();
            }, 10* 1000) as unknown as number;
        },
        playBeep(): void {
            try {
                const audio = new Audio("/sound/beep.mp3");
                audio.play();
            } catch(e) {
                // ignore errors, we just can't beep
            }
        },
        clearBeeping(): void {
            if (this.beepInterval)
                clearInterval(this.beepInterval);
            this.beepInterval = 0;
        },
        showBatteryToast(): void {
            // Don't show battery toast if shutdown countdown is active (AIR devices)
            if (this.shutdownInterval) return;
            this.clearBatteryToast();
            if (!Global.BatteryInfo) return;
            const level = Global.BatteryInfo.PercentageFull;
            this.batteryLastLevel = level;
            if (level <= 10) {
                this.batteryToast = ToastFeedback.error(FeedbackFormat.Close,
                    `Battery Life Is Critically Low ${level.toFixed(0)}%, Charge/Replace Now`,
                    undefined, true, undefined,
                    this.dismissBattery10ToastCustomer
                );
            } else {
                this.batteryToast = ToastFeedback.warning(FeedbackFormat.Close,
                    `Battery Life ${level.toFixed(0)}%, Charge/Replace Battery`,
                    undefined, true, undefined,
                    this.dismissBattery20ToastCustomer
                );
            }
        },
        dismissBattery10ToastCustomer(): void {
            this.clearBeeping();
            this.clearBatteryToast();
            this.less10WasShown = this.less20WasShown = true;
            CalibrationEventSupport.sendEvent(CalibrationEvent.BatteryLowAcknowledged10ByCustomer);
        },
        dismissBattery20ToastCustomer(): void {
            this.clearBatteryToast();
            this.less20WasShown = true;
            CalibrationEventSupport.sendEvent(CalibrationEvent.BatteryLowAcknowledged20ByCustomer);
        },
        dismissBattery10ToastCompanion(): void {
            this.clearBeeping();
            this.clearBatteryToast();
            this.less10WasShown = this.less20WasShown = true;
        },
        dismissBattery20ToastCompanion(): void {
            this.clearBatteryToast();
            this.less20WasShown = true;
        },
        clearBatteryToast(): void {
            if (this.batteryToast)
                ToastFeedback.clear(this.batteryToast);
            this.batteryToast = null;
        },

        // AIR low battery auto-shutdown methods
        startShutdownCountdown(): void {
            if (this.shutdownInterval) return; // already running
            this.clearBatteryToast(); // clear existing battery toast
            this.shutdownCountdown = 30;
            this.showShutdownToast();
            this.shutdownInterval = setInterval((): void => {
                this.updateShutdownCountdown();
            }, 1000) as unknown as number;
        },
        updateShutdownCountdown(): void {
            this.shutdownCountdown--;
            if (this.shutdownCountdown <= 0) {
                this.executeShutdown();
            } else {
                this.updateShutdownToastMessage();
            }
        },
        showShutdownToast(): void {
            this.clearShutdownToast();
            const message = this.getShutdownMessage();
            if (this.haveSession()) {
                // Show with Cancel Shutdown button when in active session
                this.shutdownToast = ToastFeedback.error(FeedbackFormat.CancelShutdown,
                    message,
                    undefined, true, undefined,
                    this.cancelShutdownCountdown
                );
            } else {
                // Show without button when not in session
                this.shutdownToast = Toast.error(message, undefined, true, { duration: false });
            }
        },
        getShutdownMessageText(): string {
            return `Low battery - shutting down in ${this.shutdownCountdown} seconds...`;
        },
        getShutdownMessage(): string {
            return `<span id="shutdown-message">${this.getShutdownMessageText()}</span>`;
        },
        updateShutdownToastMessage(): void {
            const messageEl = document.getElementById("shutdown-message");
            if (messageEl) {
                messageEl.textContent = this.getShutdownMessageText();
            } else {
                // Fallback: recreate toast if element not found
                this.showShutdownToast();
            }
        },
        clearShutdownToast(): void {
            if (this.shutdownToast) {
                if (typeof this.shutdownToast === "number") {
                    ToastFeedback.clear(this.shutdownToast);
                } else {
                    // Shown with duration false, so nothing ever schedules its unregister; destroying
                    // it without this leaves it counted as active and the camera window suppressed.
                    Toast.unregisterToast(this.shutdownToast);
                    this.shutdownToast.destroy();
                }
                this.shutdownToast = null;
            }
        },
        cancelShutdownCountdown(): void {
            this.clearShutdownCountdown();
            this.shutdownCancelled = true;
            Toast.info("Shutdown cancelled. Device will continue until battery depletes.");
        },
        executeShutdown(): void {
            this.clearShutdownCountdown();
            ToastFeedback.clearAll();
            Toast.success("Shutting down...");
            if (AUGGIEAVAILABLE) {
                setTimeout((): void => {
                    Auggie.System.ShutDown();
                }, 2000); // slight delay so the toast is shown
            }
        },
        clearShutdownCountdown(): void {
            if (this.shutdownInterval) {
                clearInterval(this.shutdownInterval);
                this.shutdownInterval = 0;
            }
            this.clearShutdownToast();
            this.shutdownCountdown = 0;
        },

        setTemperatureInfo(): void {
            if (!this.temperatureInterval) {
                const interval = Number(import.meta.env.VITE_TEMPERATUREINFOINTERVAL!);
                if (!interval) return;
                this.getTemperatureInfo();// first time retrieval
                if (!Global.TemperatureInfo) return; // no temperature info available
                if (Global.DetailLog) console.log(`setTemperatureInfo: Temperature info retrieval every ${interval} seconds`);
                this.temperatureInterval = setInterval((): void => {
                    this.getTemperatureInfo();
                }, interval * 1000) as unknown as number;
            }
        },
        clearTemperatureInfo(): void {
            if (this.temperatureInterval) {
                clearInterval(this.temperatureInterval);
                this.temperatureInterval = 0;
            }
        },
        getTemperatureInfo(): void {
            // retrieve temperature info
            if (!AUGGIEAVAILABLE)
                return; // no temperature information available
            if (!Global.IsVersionAtLeast(Global.TemperaturePackageVersion))
                return; // old package, don't retrieve temperature info
            if (Global.DetailLog) console.log("getTemperatureInfo: retrieving temperature information");
            const infoString = Auggie.Device.TemperatureInfo();
            if (infoString?.length > 0) {
                try {
                    const info = JSON.parse(infoString);
                    Global.TemperatureInfo = info;
                } catch (error) {
                    Global.TemperatureInfo = null;
                }
            }
            if (Global.DetailLog) console.log(`getTemperatureInfo: temperature information ${infoString}`);
            this.showTemperatureInfo();

            CalibrationEventSupport.sendTemperatureInformation();
        },

        showTemperatureInfo(): void {
            const info = Global.TemperatureInfo;
            if (!info?.WarningMessage) return;
            switch (info.WarningLevel) {
                case 1:
                    Toast.success(info.WarningMessage);
                    break;
                case 2:
                    Toast.warning(info.WarningMessage);
                    break;
                case 3:
                    Toast.error(info.WarningMessage);
                    break;
            }
        },

        setCpuInfo(): void {
            if (!this.cpuInterval) {
                const interval = Number(import.meta.env.VITE_CPUINFOINTERVAL!);
                if (!interval) return;
                // The first read does not gate the interval. A single call can fail transiently while
                // the device shell is out of step, and giving up on it would leave a capable unit
                // unpolled until it is rebooted. getCpuInfo() is a cheap no-op where there is nothing
                // to read, so a unit with no processor information still behaves as it does today.
                this.getCpuInfo();// first time retrieval
                if (Global.DetailLog) console.log(`setCpuInfo: Processor info retrieval every ${interval} seconds`);
                this.cpuInterval = setInterval((): void => {
                    this.getCpuInfo();
                }, interval * 1000) as unknown as number;
            }
        },
        getCpuInfo(): void {
            // retrieve processor performance info
            if (!AUGGIEAVAILABLE)
                return; // no processor information available
            if (!Global.IsVersionAtLeast(Global.CpuPackageVersion))
                return; // old package, don't retrieve processor info
            if (Global.DetailLog) console.log("getCpuInfo: retrieving processor information");
            let infoString: string;
            try {
                infoString = Auggie.Device.CpuInfo();
            } catch (error) {
                // The gate is a build number, so a client reporting new enough but predating the
                // binding would throw here, out of mounted(), and half-initialize the whole app.
                console.warn("getCpuInfo: Auggie.Device.CpuInfo is unavailable", error);
                return;
            }
            // A poll that produced nothing leaves the last reading standing. It is not a recovery, and
            // treating it as one would re-arm the alert and clear the header on a unit still degraded.
            if (!infoString?.length) return;
            try {
                Global.CpuInfo = JSON.parse(infoString);
            } catch (error) {
                console.warn(`getCpuInfo: invalid JSON "${infoString}"`, error);
                return;
            }
            if (Global.DetailLog) console.log(`getCpuInfo: processor information ${infoString}`);
            if (AppStatic.MainHeader) AppStatic.MainHeader.update();
            this.showCpuInfo();

            CalibrationEventSupport.sendCpuInformation();
        },

        showCpuInfo(): void {
            if (!isCpuDegraded(Global.CpuInfo)) {
                // Only a healthy reading re-arms. The warning band is not a recovery, so a unit
                // hovering at the limit does not re-warn on every poll as it crosses back and forth.
                if (isCpuHealthy(Global.CpuInfo)) this.cpuAlertArmed = true;
                return;
            }
            if (!this.cpuAlertArmed) return;// already warned for this episode
            // Leave an operator's own prompt alone and stay armed, so this episode is still warned
            // about on the next poll rather than replacing what they are looking at.
            if (isAlertDialogOpen()) return;
            this.cpuAlertArmed = false;
            // Restart is alt1 rather than cancel because cancel is the product-wide decline result and
            // choosing to restart is not a decline. Either slot renders to the left of Continue.
            showAlertDialog(CPUDEGRADEDMESSAGE, undefined, { alt1Button: "Restart", okButton: "Continue" })
                .then((button: ButtonClicked): void => {
                    if (button === ButtonClicked.Alt1 && AUGGIEAVAILABLE)
                        Auggie.System.Restart();
                });
        },

        // The native camera window is always on top of the browser, so it is hidden while anything
        // needs the screen. A camera session started while suppressed re-applies this, because starting
        // the render clears the client's hide request.
        setCameraWindowSuppressed(suppressed: boolean): void {
            if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(Global.CameraWindowVisibilityVersion)) return;
            if (suppressed)
                Auggie.Camera.HideCameraWindow();
            else
                Auggie.Camera.ShowCameraWindow();
        },

        setCellularPolls(): void {
            // APD-13177: signal every 30s (icon bars + color), info every 2min (R badge + cellular info page data).
            if (!AUGGIEAVAILABLE) return;
            if (!Global.IsVersionAtLeast(Global.CellularPackageVersion)) return;
            this.getCellularSignal();
            this.getCellularInfo();
            if (!this.cellularSignalInterval) {
                this.cellularSignalInterval = setInterval((): void => {
                    this.getCellularSignal();
                }, 30 * 1000) as unknown as number;
            }
            if (!this.cellularInfoInterval) {
                this.cellularInfoInterval = setInterval((): void => {
                    this.getCellularInfo();
                }, 120 * 1000) as unknown as number;
            }
        },
        clearCellularPolls(): void {
            if (this.cellularSignalInterval) {
                clearInterval(this.cellularSignalInterval);
                this.cellularSignalInterval = 0;
            }
            if (this.cellularInfoInterval) {
                clearInterval(this.cellularInfoInterval);
                this.cellularInfoInterval = 0;
            }
        },
        getCellularSignal(): void {
            if (!AUGGIEAVAILABLE) return;
            if (!Global.IsVersionAtLeast(Global.CellularPackageVersion)) return;
            const infoString = Auggie.Cellular.CellularSignal();
            if (infoString?.length > 0) {
                try {
                    Global.CellularSignal = JSON.parse(infoString);
                } catch (error) {
                    Global.CellularSignal = null;
                    console.warn(`getCellularSignal: invalid JSON "${infoString}"`, error);
                }
            } else {
                Global.CellularSignal = null;
            }
            if (Global.DetailLog) console.log(`getCellularSignal: ${infoString}`);
            if (AppStatic.MainHeader) AppStatic.MainHeader.update();
        },
        getCellularInfo(): void {
            if (!AUGGIEAVAILABLE) return;
            if (!Global.IsVersionAtLeast(Global.CellularPackageVersion)) return;
            const infoString = Auggie.Cellular.CellularInfo();
            if (infoString?.length > 0) {
                try {
                    Global.CellularInfo = JSON.parse(infoString);
                } catch (error) {
                    Global.CellularInfo = null;
                    console.warn(`getCellularInfo: invalid JSON "${infoString}"`, error);
                }
            } else {
                Global.CellularInfo = null;
            }
            if (Global.DetailLog) console.log(`getCellularInfo: ${infoString}`);
            if (AppStatic.MainHeader) AppStatic.MainHeader.update();
        },

        setSessionVerification(): void {
            if (Global.IsDemo()) return;
            if (!this.verificationInterval) {
                const interval = Number(import.meta.env.VITE_VERIFYSESSIONINTERVAL!);
                if (!interval) return;
                if (Global.DetailLog) console.log(`setSessionVerification: Session verification every ${interval} seconds`);
                this.verificationInterval = setInterval((): void => {
                    this.verifySession();
                }, interval * 1000) as unknown as number;
            }
        },
        clearSessionVerification(): void {
            if (this.verificationInterval) {
                clearInterval(this.verificationInterval);
                this.verificationInterval = 0;
            }
        },
        verifySession(): void {
            if (Global.CalibrationSession === null || Global.CalibrationSession.CalibrationSessionGuid === null) return;
            const sessionGuid = Global.CalibrationSession.CalibrationSessionGuid;
            const dr = new DataRequest();
            dr.autoToastOnFailure = false;// don't show failure popup
            dr.$get<UpdateResultDto>("/Service/CalibrationSession/VerifySession", { CalibrationSessionGuid: sessionGuid })
                .then((updateResult: UpdateResultDto): void => {
                    if (Global.CalibrationSession?.CalibrationSessionGuid !== sessionGuid) {
                        if (Global.DetailLog) console.log(`verifySession: Session ${sessionGuid} was replaced, result ignored`);
                        return;
                    }
                    if (!updateResult.Success) {
                        if (Global.DetailLog) console.log(`verifySession: Session ${sessionGuid} does NOT exist`);
                        this.restartSession("The calibration session is no longer valid, a new session will be started.");
                    } else {
                        if (Global.DetailLog) console.log(`verifySession: Session ${sessionGuid} exists`);
                    }
                })
                .catch((reason: any): void => {
                    // we still don't know, ignore this failure
                });
        },

        // Connection Overlay

        showConnectionOverlay(): void {
            this.$nextTick((): void => {
                if ((this.$refs as any).refConnectionOverlay) {
                    (this.$refs as any).refConnectionOverlay.show();
                }
            });
        },
        hideConnectionOverlay(): void {
            this.$nextTick((): void => {
                if ((this.$refs as any).refConnectionOverlay) {
                    (this.$refs as any).refConnectionOverlay.hide();
                }
            });
        },

        // SignalR event handlers

        signalRReconnecting(): void {
            console.log("SignalR reconnecting");
            this.showConnectionOverlay();
            if (AppStatic.MainHeader) AppStatic.MainHeader.updateNetworkStatus();
        },
        signalRReconnected(): void {
            console.log("SignalR reconnected");
            if (AppStatic.MainHeader) AppStatic.MainHeader.updateNetworkStatus();
            if (SignalR.getDeviceStatus() !== DeviceStatus.Valid) {
                this.hideConnectionOverlay();
                return;
            }
            // Register before checking the session: once the new connection is registered, a late
            // disconnect of the old one can no longer end the session behind the check.
            SignalR.reregisterDevice()
                .then((accepted: boolean): void => {
                    if (accepted) {
                        this.hideConnectionOverlay();
                        this.verifySession();
                    } else {
                        this.restartSession("The device could not reconnect to the server, the session will be restarted.");
                    }
                });
        },
        signalRDisconnected(): void {
            console.log("SignalR Disconnected");
            if (AppStatic.MainHeader) AppStatic.MainHeader.updateNetworkStatus();
            this.restartSession("The server connection (SignalR) has been lost, the session will be restarted.");
        },
        restartSession(message: string): void {
            if (this.restarting) return;
            this.restarting = true;
            Toast.error(message);
            this.hideConnectionOverlay();
            // Cleared at the reload, not before: the current page stays mounted until then and its
            // in-flight requests still dereference the session.
            setTimeout((): void => {
                try {
                    this.clearSession();
                } finally {
                    globalThis.location.assign("/");
                }
            }, 3*1000);
        }
    }
});

// Export static accessors
export { AppStatic };

</script>
